// Pre-generation photo quality gate.
//
// Real pixel analysis — NOT Clostech's theatrical "Iluminación 100 %". We measure
// actual resolution and average luma and give honest, qualitative, ES-AR guidance
// so a shopper can retake a weak photo BEFORE we spend an inference call.
//
// Two tiers of severity:
//   "warn"  advisory (dim / small): the shopper may still proceed.
//   "block" the photo cannot yield a valid try-on (a face-only headshot: there is
//           no body to dress). The caller disables generation until it is retaken.
// A headshot is the one weak photo we DO stop, because it guarantees a wrong-body
// render and a wasted paid call. Framing detection is progressive-enhancement via
// the browser's native FaceDetector; where it is unavailable we fail OPEN (never
// block) and the server-side body check backstops it.
// The scoring halves are pure and unit-testable; only measurement touches the DOM.

import type { Strings } from "./strings"

export type PhotoVerdict = {
  level: "good" | "warn" | "block"
  hint: string // shopper-facing; empty when we could not analyze
}

export type PhotoMetrics = {
  width: number
  height: number
  brightness: number // 0..255 average Rec.601 luma
}

// Two size floors on the short side of the photo:
//   MIN_USABLE   — hard BLOCK: too small to produce any usable try-on (disable submit).
//   MIN_DIMENSION — advisory WARN: below it we flag "foto chica" but let the shopper
//                   proceed; the warning doubles as a quality disclaimer ("your call").
// 256 as the block floor: below ~256px a full-body subject's face/body is ~30px — too
// little for the model to preserve identity or render detail. The gate validated clean
// renders at 398px, which sits safely in the advisory (warn) band, not the block band.
const MIN_USABLE = 256
const MIN_DIMENSION = 512
const DARK = 55 // avg luma below → too dark
const BRIGHT = 225 // above → washed out / backlit
// A face taller than this fraction of the frame is a headshot/selfie, not a body
// photo. Waist-up shots sit well under it (~0.15–0.25); full-body lower still.
const HEADSHOT_FACE_RATIO = 0.4

// Pure: metrics → verdict. No DOM, so it is trivially unit-testable. Copy is
// supplied by the caller's language bundle.
export function scorePhoto(m: PhotoMetrics, s: Strings): PhotoVerdict {
  const short = Math.min(m.width, m.height)
  if (short < MIN_USABLE) {
    return { level: "block", hint: s.photoBlockTooSmall }
  }
  if (short < MIN_DIMENSION) {
    return { level: "warn", hint: s.photoWarnSmall }
  }
  if (m.brightness < DARK) {
    return { level: "warn", hint: s.photoWarnDark }
  }
  if (m.brightness > BRIGHT) {
    return { level: "warn", hint: s.photoWarnBright }
  }
  return { level: "good", hint: s.photoGood }
}

// Pure: face-height fraction → framing verdict, or null when there is nothing to
// object to. A null face ratio means "could not detect" and must NEVER block.
export function scoreFraming(faceHeightRatio: number | null, s: Strings): PhotoVerdict | null {
  if (faceHeightRatio !== null && faceHeightRatio >= HEADSHOT_FACE_RATIO) {
    return { level: "block", hint: s.photoBlockHeadshot }
  }
  return null
}

// Extract metrics from a File via a small offscreen canvas (downscaled sample for
// a fast average — we do not need full resolution to estimate luma).
export async function measurePhoto(file: File): Promise<PhotoMetrics> {
  const bitmap = await createImageBitmap(file)
  const width = bitmap.width
  const height = bitmap.height

  const sample = 64
  const canvas = document.createElement("canvas")
  canvas.width = sample
  canvas.height = sample
  const ctx = canvas.getContext("2d")
  if (!ctx) {
    bitmap.close?.()
    throw new Error("no_canvas_context")
  }
  ctx.drawImage(bitmap, 0, 0, sample, sample)
  const { data } = ctx.getImageData(0, 0, sample, sample)
  bitmap.close?.()

  let total = 0
  for (let i = 0; i < data.length; i += 4) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]
  }
  const brightness = total / (data.length / 4)

  return { width, height, brightness }
}

// The browser's native FaceDetector (Shape Detection API) is experimental and not
// in the DOM lib typings, so we type just the sliver we use and feature-detect it.
type FaceBox = { boundingBox: { height: number } }
interface FaceDetectorLike { detect(source: CanvasImageSource): Promise<FaceBox[]> }
type FaceDetectorCtor = new (opts?: { fastMode?: boolean; maxDetectedFaces?: number }) => FaceDetectorLike

// Tallest detected face as a fraction of image height, or null when we cannot
// tell — no FaceDetector, no face found, or any error. Null NEVER blocks.
export async function detectFaceRatio(file: File): Promise<number | null> {
  const Ctor = (globalThis as unknown as { FaceDetector?: FaceDetectorCtor }).FaceDetector
  if (!Ctor) return null

  let bitmap: ImageBitmap | null = null
  try {
    bitmap = await createImageBitmap(file)
    const faces = await new Ctor({ fastMode: true, maxDetectedFaces: 5 }).detect(bitmap)
    if (!faces.length) return null
    const tallest = Math.max(...faces.map((f) => f.boundingBox.height))
    return tallest / bitmap.height
  } catch {
    return null // unsupported / blocked / odd format → fail open
  } finally {
    bitmap?.close?.()
  }
}

// Full assessment. Framing (a headshot → "block") outranks the advisory
// resolution/brightness checks. On any failure to analyze (odd format, canvas
// blocked in a hardened browser), returns an empty-hint "good" — we NEVER block
// the shopper on our own inability to measure.
export async function assessPhoto(file: File, s: Strings): Promise<PhotoVerdict> {
  try {
    const [metrics, faceRatio] = await Promise.all([ measurePhoto(file), detectFaceRatio(file) ])
    return scoreFraming(faceRatio, s) ?? scorePhoto(metrics, s)
  } catch {
    return { level: "good", hint: "" }
  }
}
